import React from "react";
import { graphql, useStaticQuery } from "gatsby";
import tw, { styled } from "twin.macro";
import { useInView } from "react-intersection-observer";

import Copyright from "./Copyright";
import Parallax from "./Parallax";
import Form from "./Form";

const Container = styled.footer(({ contact }) => [
  tw`sm:px-8 lg:px-12 bg-dark flex flex-col items-center justify-center w-full max-w-full px-4 pt-20 overflow-hidden text-white`,

  contact ? tw`lg:pt-48` : tw`lg:pt-32`,
]);

const Title = tw.p`font-mazuis italic uppercase text-5xl sm:text-7xl text-center lg:text-8xl xl:text-9xl mb-6 mt-24`;

const Subtitle = () => [
  tw`text-primary sm:text-5xl lg:text-6xl xl:text-7xl font-mazuis mb-24 text-4xl italic text-center`,
];
const SubH2 = styled.h2(Subtitle);
const SubH1 = styled.h1(Subtitle);

const Top = tw.div`relative w-full flex flex-col max-w-[90rem] items-center h-[10rem] before:(content[''] bg-white bg-opacity-20 w-full max-w-[80vw] absolute bottom-[1rem] left-0 right-0 mx-auto h-px)`;

const Bottom = tw.div`flex flex-col-reverse lg:flex-row max-w-[90rem] relative w-full pt-48 lg:pt-40 before:(content[''] bg-white bg-opacity-20 w-full absolute top-[1rem] left-0 h-px max-w-[80vw] right-0 mx-auto)`;

const Divider = tw.div`bg-white flex flex-col items-center bg-opacity-20 absolute top-0 left-0 right-0 mx-auto w-px h-[8rem] lg:h-full before:(content[''] bg-white bg-opacity-20 w-[3px] h-[3px] rounded-full absolute top-[-1rem])`;

const Left = tw.div`grid grid-cols-1 relative w-full mt-12 lg:(w-1/2 mt-0) items-center justify-start gap-32 sm:gap-60`;

const Right = tw.div`flex flex-col relative w-full lg:(w-1/2 pl-8 items-end) items-center justify-center pb-20`;

const AddressWrapper = styled.li(({ inview }) => [
  tw`sm:text-4xl font-mazuis flex flex-col items-center text-3xl italic leading-snug text-center translate-y-[50px] opacity-0 duration-1000 ease-in-out`,
  inview && tw`blur-0 translate-y-0 opacity-100`,
]);

const Addresses = tw.ul`grid grid-cols-1 items-center justify-start gap-20`;
const Address = tw.address`flex flex-col items-center justify-start mb-6`;
const LocationName = tw.p`mb-6 relative text-5xl pb-4 flex flex-col items-center text-primary  after:(content[''] w-[calc(100% + 10rem)] max-w-[80vw] h-px bg-white bg-opacity-20 absolute bottom-0 )`;
const PhoneNumber = tw.a`text-primary hover:text-gold duration-500 ease-in-out`;
const Directions = tw.a`not-italic font-gothic flex uppercase tracking-widest hover:text-gold transition-colors duration-500 ease-in-out text-base mt-8`;

const fields = [
  {
    type: "text",
    required: true,
    label: "Name",
    id: "footer-f-name",
    width: 1,
  },
  {
    type: "tel",
    required: true,
    label: "Phone Number",
    id: "footer-f-phone",
    width: 1,
  },
  {
    type: "email",
    required: true,
    label: "Email",
    id: "footer-f-email",
    width: 1,
  },
  {
    type: "textarea",
    required: true,
    label: "Tell us what happened  —  give us the details of your case",
    id: "footer-f-description",
    width: "full",
  },
];

const Footer = ({ contact, h1 }) => {
  const { esquireAcf, acfOptionsFooter } = useStaticQuery(graphql`
    {
      wp {
        esquireAcf {
          firmInformation {
            locations {
              streetAddress {
                line
              }
              phoneNumber
              mapsLink
              title
            }
          }
        }
        acfOptionsFooter {
          footer {
            title: fTitle
            subtitle: fSubtitle
            images: fImages {
              background {
                ...GatsbyImage_Blur
              }
              foreground {
                ...GatsbyImage_Blur
              }
            }
          }
        }
      }
    }
  `).wp;
  let { locations } = esquireAcf.firmInformation;
  const { title, subtitle, images } = acfOptionsFooter.footer;

  locations.forEach((el) => {
    const [ref, inView] = useInView({
      threshold: 0.15,
      triggerOnce: true,
    });

    el.observer = ref;
    el.inView = inView;
  });

  const SubActual = h1 ? SubH1 : SubH2;
  return (
    <Container contact={contact}>
      <Top>
        <Divider tw="scale-y-[-1] bottom-0 top-auto" />
      </Top>
      <Title>{title}</Title>
      <SubActual>{subtitle}</SubActual>
      <Bottom>
        <Left>
          <Addresses>
            {locations.map(
              (
                {
                  streetAddress,
                  observer,
                  inView,
                  phoneNumber,
                  mapsLink,
                  title,
                },
                i
              ) => (
                <AddressWrapper key={i} ref={observer} inview={inView}>
                  <LocationName>{title}</LocationName>
                  <Address>
                    {streetAddress.map(({ line }, i) => (
                      <p key={i}>{line}</p>
                    ))}
                  </Address>
                  <PhoneNumber href={`tel:${phoneNumber}`}>
                    {phoneNumber}
                  </PhoneNumber>
                  <Directions href={mapsLink}>Map + Directions</Directions>
                </AddressWrapper>
              )
            )}
          </Addresses>
          {/* <Parallax
            footer
            foreground={images.foreground}
            background={images.background}
          /> */}
        </Left>
        <Divider />
        <Right id="footer-contact-form">
          <Form cols={1} tw="max-w-[35rem]" fields={fields} />
        </Right>
      </Bottom>
      <Copyright />
    </Container>
  );
};

export default Footer;
